import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import ConversationApi from 'dashboard/api/conversations';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import {
  phoneNumbersMatch,
  normalizePhoneNumber,
} from 'dashboard/helper/phoneNumberHelper';

/**
 * Converts a local Israeli number (05...) to international format (+972...).
 */
const toInternationalFormat = phone => {
  const normalized = normalizePhoneNumber(phone);
  if (!normalized) return normalized;

  if (normalized.startsWith('05') && normalized.length === 10) {
    return `+972${normalized.slice(1)}`;
  }

  if (normalized.startsWith('+')) return normalized;

  if (normalized.startsWith('972') && normalized.length >= 12) {
    return `+${normalized}`;
  }

  return normalized;
};

/**
 * @param {Object} options
 * @param {Function} [options.promptContactName] - async function that returns a name string or null
 */
export function usePhoneClick({ promptContactName } = {}) {
  const router = useRouter();
  const route = useRoute();
  const selectedChat = useMapGetter('getSelectedChat');

  const navigateToConversation = (accountId, conversationId) => {
    router.push(
      frontendURL(
        conversationUrl({
          accountId,
          id: conversationId,
        })
      )
    );
  };

  const handlePhoneClick = async phoneNumber => {
    const accountId = route.params.accountId;
    const currentInboxId = selectedChat.value?.inbox_id;

    if (!currentInboxId) {
      useAlert('Could not determine the current inbox.');
      return;
    }

    const internationalNumber = toInternationalFormat(phoneNumber);

    try {
      // 1. Search for contacts — try international format first, then original
      let contacts = [];
      const searchQueries = [
        ...new Set([internationalNumber, phoneNumber]),
      ];

      for (const query of searchQueries) {
        if (!query) continue;
        const { data: searchData } = await ContactAPI.search(query);
        contacts = searchData.payload || [];
        if (contacts.length > 0) break;
      }

      // Find a contact whose phone matches
      const contact = contacts.find(
        c =>
          phoneNumbersMatch(c.phone_number, phoneNumber) ||
          phoneNumbersMatch(c.phone_number, internationalNumber)
      );

      if (contact) {
        // 2. Check for existing conversations on the current inbox
        const { data: convData } = await ContactAPI.getConversations(
          contact.id
        );
        const conversations = convData.payload || [];

        const existingConv = conversations.find(
          c => c.inbox_id === currentInboxId
        );

        if (existingConv) {
          navigateToConversation(accountId, existingConv.id);
          return;
        }

        // 3. No existing conversation on this inbox - create one
        const { data: newConv } = await ConversationApi.create({
          inbox_id: currentInboxId,
          contact_id: contact.id,
        });

        navigateToConversation(accountId, newConv.id);
        return;
      }

      // 4. No matching contact found - prompt for name, then create contact + conversation
      let contactName = internationalNumber || phoneNumber;
      if (promptContactName) {
        const name = await promptContactName(phoneNumber);
        if (name === null) return; // User cancelled
        if (name) contactName = name;
      }

      const {
        data: {
          payload: { contact: createdContact },
        },
      } = await ContactAPI.create({
        phone_number: internationalNumber || phoneNumber,
        name: contactName,
      });

      const { data: newConv } = await ConversationApi.create({
        inbox_id: currentInboxId,
        contact_id: createdContact.id,
      });

      navigateToConversation(accountId, newConv.id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Phone click error:', error);
      useAlert('Failed to open conversation for this phone number.');
    }
  };

  return { handlePhoneClick };
}
